"use client";

import { useEffect, useState } from "react";
import fileLinesManifest from "@/lib/debug/file-lines.json";
import { requestDebugFileLines } from "@/lib/api/transports/debug-file-lines-transport";
import {
  getLineCountColor,
  normalizeFilePath,
} from "./file-lines-formatting.mjs";

export { getLineCountColor, normalizeFilePath };

// In-memory cache for line counts, pre-populated with manifest
const fileLineCache: Record<string, number> = {
  ...(fileLinesManifest as Record<string, number>),
};

/**
 * Get the line count for a given file path.
 * Returns null if the line count is not known.
 */
export function getFileLineCount(path: string): number | null {
  const normalized = normalizeFilePath(path);
  if (fileLineCache[normalized] !== undefined) {
    return fileLineCache[normalized];
  }
  return null;
}

/**
 * Format path with line count suffix, e.g. "app/.../page.tsx:12345"
 */
export function formatPathWithLineCount(path: string): string {
  const count = getFileLineCount(path);
  return count !== null ? `${path}:${count}` : path;
}

/**
 * Refresh line counts from live dev API route if running in development mode
 */
export async function refreshFileLines(paths: string[]): Promise<void> {
  if (typeof window === "undefined" || process.env.NODE_ENV !== "development") {
    return;
  }
  const cleanPaths = Array.from(
    new Set(paths.map(normalizeFilePath).filter(Boolean)),
  );
  if (cleanPaths.length === 0) return;

  try {
    const data = await requestDebugFileLines(cleanPaths);
    for (const [key, val] of Object.entries(data)) {
      if (Number.isFinite(val)) fileLineCache[normalizeFilePath(key)] = val;
    }
  } catch {
    // Ignore dev fetch errors; manifest cache is already in place
  }
}

/**
 * React hook to keep line counts fresh for a set of paths in development
 */
export function useFileLines(paths: string[]) {
  const [, setTick] = useState(0);
  const pathKey = paths.join(",");

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    let cancelled = false;
    const targetList = pathKey.split(",").filter(Boolean);
    void refreshFileLines(targetList).then(() => {
      if (!cancelled) setTick((t) => t + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [pathKey]);
}
